/**
 * Story 062 — Component Models list page (`/component-models`).
 *
 * Mirrors `TurbineModelsPage` structure (Story 059) — same toolbar
 * layout, footer, modal chrome. The deltas are:
 *   - Columns: Code + Name + Category + Manufacturer + Scope +
 *     View/Edit (pinned).
 *   - Filters: Search + Category (multi) + Scope (single) +
 *     Show-inactive switch.
 *   - Column-visibility key: `aero.component-models.columns`.
 *
 * UX brief: `docs/ux/062-component-models-design-brief.md`.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Popover,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import type { GridColDef, GridSortModel } from "@mui/x-data-grid";
import {
  ColumnVisibilityButton,
  DataPageFooter,
  DataPageShell,
  DataPageToolbar,
  DataTable,
  DensityToggleButton,
  MultiSelectChipFilter,
  ScopeChip,
  SearchField,
  SingleSelectFilter,
  sidebar as sidebarTokens,
  typography,
  useCanEditResourceFor,
  type ColumnDescriptor,
  type Density,
} from "@aeriosiq/ui";
import { useSearchParams } from "react-router-dom";
import { ToastProvider } from "../account/ToastContext.js";
import {
  useListComponentGroupsQuery,
  useListComponentModelsQuery,
  type AssetComponentModelExpanded,
  type ComponentGroup,
} from "./queries/index.js";
import { InactiveBadge } from "../sites/shared/OffshoreChip.js";
import { AddComponentModelDialog } from "./modal-add/AddComponentModelDialog.js";
import { EditComponentModelDialog } from "./modal-edit/EditComponentModelDialog.js";
import { findGroupNameById } from "./shared/componentGroupMapping.js";
import { BoxIcon, InfoIcon, PencilIcon, PlusIcon } from "@aeriosiq/ui/icons";

const COLUMNS_STORAGE_KEY = "aero.component-models.columns";

const COMPONENT_MODEL_COLUMNS_META: ColumnDescriptor[] = [
  { field: "name", headerName: "Name" },
  { field: "componentGroup", headerName: "Component Group" },
  { field: "manufacturer", headerName: "Manufacturer" },
  { field: "scope", headerName: "Scope" },
  { field: "edit", headerName: "Edit", pinned: true },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
const DEFAULT_PAGE_SIZE = 25;

type SortKey = "name" | "manufacturer" | "scope" | "componentGroup";
type SortDir = "asc" | "desc";

const SORTABLE_FIELDS: ReadonlySet<string> = new Set<string>([
  "name",
  "manufacturer",
  "scope",
  "componentGroup",
]);

function normaliseSortField(field: string): SortKey | null {
  if (
    field === "name" ||
    field === "manufacturer" ||
    field === "scope" ||
    field === "componentGroup"
  ) {
    return field;
  }
  return null;
}

function parseSort(
  value: string | null,
): { key: SortKey; dir: SortDir } | null {
  if (!value) return null;
  const [k, d] = value.split(":");
  if (!k || !d) return null;
  const key = normaliseSortField(k);
  if (!key) return null;
  if (d !== "asc" && d !== "desc") return null;
  return { key, dir: d };
}

function parsePageSize(value: string | null): number {
  const n = Number(value ?? "");
  if (
    !Number.isFinite(n) ||
    !PAGE_SIZE_OPTIONS.includes(n as (typeof PAGE_SIZE_OPTIONS)[number])
  ) {
    return DEFAULT_PAGE_SIZE;
  }
  return n;
}

function parseCsv(value: string | null): string[] {
  if (!value) return [];
  const seen = new Set<string>();
  for (const raw of value.split(",")) {
    const v = raw.trim();
    if (v.length > 0) seen.add(v);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

function parseScope(value: string | null): "global" | "tenant" | undefined {
  if (value === "global") return "global";
  if (value === "tenant") return "tenant";
  return undefined;
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

// SB2-CC-04 — legacy `MUTATE_ROLES_FOR_ADD` table replaced by
// `useCanEditResourceFor("component_model.create_tenant")`. Kept only
// as a code-comment landmark for the next refactor.

export function ComponentModelsPage(): ReactElement {
  return (
    <ToastProvider>
      <ComponentModelsPageInner />
    </ToastProvider>
  );
}

function ComponentModelsPageInner(): ReactElement {
  const [params, setParams] = useSearchParams();
  const modelIdInUrl = params.get("model");
  const isNewOpen = params.get("new") === "1";
  // SB2-W-07 — `?new=global` opens the global-scope Add dialog.
  const isNewGlobalOpen = params.get("new") === "global";
  const includeInactive = params.get("includeInactive") === "1";

  const [searchInput, setSearchInput] = useState(params.get("search") ?? "");
  const debouncedSearch = useDebounced(searchInput, 500);

  const [density, setDensity] = useState<Density>("compact");

  // See SitesPage for the rationale — react-router v7 setSearchParams
  // is reference-unstable on URL change; without this ref guard the
  // search-mirror effect re-fires on every navigation and strips the
  // page param via `next.delete("page")`.
  const lastAppliedSearchRef = useRef(params.get("search") ?? "");

  // Mirror search into URL on debounced change.
  useEffect(() => {
    if (lastAppliedSearchRef.current === debouncedSearch) return;
    lastAppliedSearchRef.current = debouncedSearch;
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (debouncedSearch.length > 0) {
          next.set("search", debouncedSearch);
        } else {
          next.delete("search");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }, [debouncedSearch, setParams]);

  const sortParsed = parseSort(params.get("sort"));
  const pageSize = parsePageSize(params.get("pageSize"));
  const page = Math.max(1, Number(params.get("page") ?? "1") || 1);

  const componentGroupParam = params.get("componentGroupId");
  const componentGroupSelected = useMemo(
    () => parseCsv(componentGroupParam),
    [componentGroupParam],
  );

  const manufacturerParam = params.get("manufacturer");
  const manufacturerSelected = useMemo(
    () => parseCsv(manufacturerParam),
    [manufacturerParam],
  );

  const scopeSelected = parseScope(params.get("scope"));

  // Component-group catalogue. Drives the Component Group filter
  // dropdown options AND the column-cell name-lookup (resolves the
  // row's `componentGroupId` → display name). Cacheable, near-static.
  const { data: groupsData } = useListComponentGroupsQuery();
  const componentGroups = useMemo<ComponentGroup[]>(
    () => groupsData?.items ?? [],
    [groupsData?.items],
  );

  // R58-W-06 (assets/v1@4.0.0+): the BE accepts `componentGroupId`
  // (UUID CSV) directly — no translation needed. Pre-R58-W-06 the FE
  // ran `componentGroupIdsToCategoryCsv` to emit the legacy
  // `category=<csv>` wire format, which the post-4.0.0 BE silently
  // dropped.

  const queryParams = useMemo(() => {
    const out: {
      page: number;
      pageSize: number;
      search?: string;
      sort?: string;
      includeInactive?: boolean;
      componentGroupId?: readonly string[];
      manufacturer?: readonly string[];
      scope?: "global" | "tenant";
    } = { page, pageSize };
    if (debouncedSearch.length > 0) out.search = debouncedSearch;
    if (sortParsed) out.sort = `${sortParsed.key}:${sortParsed.dir}`;
    if (includeInactive) out.includeInactive = true;
    if (componentGroupSelected.length > 0)
      out.componentGroupId = componentGroupSelected;
    if (manufacturerSelected.length > 0)
      out.manufacturer = manufacturerSelected;
    if (scopeSelected !== undefined) out.scope = scopeSelected;
    return out;
  }, [
    page,
    pageSize,
    debouncedSearch,
    sortParsed,
    includeInactive,
    componentGroupSelected,
    manufacturerSelected,
    scopeSelected,
  ]);

  const { data, isPending, isError } = useListComponentModelsQuery(queryParams);

  const [columnVisibility, setColumnVisibility] = useState<
    Record<string, boolean>
  >(() => {
    if (typeof window === "undefined") return {};
    try {
      const raw = window.localStorage.getItem(COLUMNS_STORAGE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as unknown;
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, boolean>;
      }
      return {};
    } catch {
      return {};
    }
  });

  const handleColumnVisibilityChange = useCallback(
    (next: Record<string, boolean>) => {
      setColumnVisibility(next);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(
            COLUMNS_STORAGE_KEY,
            JSON.stringify(next),
          );
        } catch {
          // Best-effort.
        }
      }
    },
    [],
  );

  function openModel(id: string): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("model", id);
        return next;
      },
      { replace: false },
    );
  }
  function closeModel(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("model");
        return next;
      },
      { replace: false },
    );
  }
  function openNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "1");
        return next;
      },
      { replace: false },
    );
  }
  function openNewGlobal(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("new", "global");
        return next;
      },
      { replace: false },
    );
  }
  function closeNew(): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("new");
        return next;
      },
      { replace: false },
    );
  }

  function setComponentGroupSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        // Migrate-away from the legacy `category=<csv>` URL param so a
        // user with a stale bookmark doesn't double-filter.
        next.delete("category");
        if (canonical.length === 0) {
          next.delete("componentGroupId");
        } else {
          next.set("componentGroupId", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setManufacturerSelected(values: readonly string[]): void {
    const canonical = [...new Set(values.map((c) => c.trim()))]
      .filter((c) => c.length > 0)
      .sort((a, b) => a.localeCompare(b));
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (canonical.length === 0) {
          next.delete("manufacturer");
        } else {
          next.set("manufacturer", canonical.join(","));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setScopeSelected(value: "global" | "tenant" | undefined): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === undefined) {
          next.delete("scope");
        } else {
          next.set("scope", value);
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function toggleIncludeInactive(checked: boolean): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (checked) {
          next.set("includeInactive", "1");
        } else {
          next.delete("includeInactive");
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }
  function setPage(p: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (p <= 1) {
          next.delete("page");
        } else {
          next.set("page", String(p));
        }
        return next;
      },
      { replace: true },
    );
  }
  function setPageSize(n: number): void {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === DEFAULT_PAGE_SIZE) {
          next.delete("pageSize");
        } else {
          next.set("pageSize", String(n));
        }
        next.delete("page");
        return next;
      },
      { replace: true },
    );
  }

  // `categories[]` rollup was removed in assets/v1@4.0.0 (FEAT-013) in
  // favour of `componentGroups[]`. The Add dialog still surfaces a
  // free-text category Autocomplete, so derive the suggestions from
  // the current page's items. Reading the deprecated `category` here
  // is the workaround until the Add dialog moves to a Component
  // Group picker — tracked as `.vibe/73 §3.3` (design-debt cleanup).
  const categoryOptions = useMemo(() => {
    const seen = new Set<string>();
    for (const m of data?.items ?? []) {
      // eslint-disable-next-line @typescript-eslint/no-deprecated -- .vibe/73 §3.3
      if (m.category) seen.add(m.category);
    }
    return Array.from(seen).sort((a, b) => a.localeCompare(b));
  }, [data?.items]);
  const manufacturerOptions = data?.manufacturers ?? [];

  // SB2-CC-04 — matrix-driven Add gate.
  const canAdd = useCanEditResourceFor("component_model.create_tenant");
  // SB2-W-07 — global-scope create (platform_admin only).
  const canAddGlobal = useCanEditResourceFor("component_model.create_global");

  const total = data?.total ?? 0;

  const sortModel = useMemo<GridSortModel>(() => {
    if (!sortParsed) return [];
    // Grid field === sort key for the surviving columns
    // (`name`, `manufacturer`, `scope`). The Component Model Code
    // column was dropped 2026-05-16 (it duplicated Name's signal).
    return [{ field: sortParsed.key, sort: sortParsed.dir }];
  }, [sortParsed]);

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const head = model[0];
      if (!head?.sort) {
        setParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete("sort");
            return next;
          },
          { replace: true },
        );
        return;
      }
      if (!SORTABLE_FIELDS.has(head.field)) return;
      const normalised = normaliseSortField(head.field);
      if (!normalised) return;
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set("sort", `${normalised}:${head.sort ?? "asc"}`);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const columns = useMemo<GridColDef<AssetComponentModelExpanded>[]>(
    () => [
      {
        field: "name",
        headerName: "Name",
        sortable: true,
        flex: 2,
        minWidth: 200,
        renderCell: (p) => (
          <Stack direction="row" alignItems="center" spacing={0}>
            <Typography variant="body2" component="span">
              {p.row.name}
            </Typography>
            {!p.row.isActive ? <InactiveBadge /> : null}
          </Stack>
        ),
      },
      {
        // Resolved Component Group name. Sourced from the row's
        // `componentGroupId` looked up against the listComponentGroups
        // catalogue. The legacy free-text `category` column is
        // deprecated (spec 082) and intentionally not rendered.
        field: "componentGroup",
        headerName: "Component Group",
        // R58-W-06 / assets/v1@4.3.0: the BE sort enum gained
        // `componentGroup:asc/desc`. The list endpoint resolves
        // ordering against `public.global_component_groups.name->>'en'`
        // (matches the resolved label rendered here).
        sortable: true,
        width: 200,
        renderCell: (p) => {
          const groupName = findGroupNameById(
            p.row.componentGroupId,
            componentGroups,
          );
          return groupName ? (
            <Typography variant="body2" component="span">
              {groupName}
            </Typography>
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },
      {
        field: "manufacturer",
        headerName: "Manufacturer",
        sortable: true,
        flex: 1,
        minWidth: 160,
        renderCell: (p) =>
          p.row.manufacturer ? (
            <Typography variant="body2" component="span">
              {p.row.manufacturer}
            </Typography>
          ) : (
            <Typography component="span" variant="body2" color="text.secondary">
              —
            </Typography>
          ),
      },
      {
        field: "scope",
        headerName: "Scope",
        sortable: true,
        width: 120,
        renderCell: (p) => <ScopeChip scope={p.row.scope} />,
      },
      {
        field: "edit",
        headerName: "Edit",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 96,
        renderCell: (p) => (
          <Button
            size="small"
            variant="text"
            startIcon={<PencilIcon fontSize="small" />}
            onClick={(e) => {
              e.stopPropagation();
              openModel(p.row.id);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    // openModel is stable over setParams (react-router ref-stable).
    // Component-group catalogue IS a real dep — column-cell rendering
    // resolves `componentGroupId` against it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [componentGroups],
  );

  return (
    <DataPageShell title="Component Models">
      <DataPageToolbar
        comfortableFilters
        ariaLabel="Component models toolbar"
        filtersTestId="component-models-toolbar-filters"
        settingsTestId="component-models-toolbar-settings"
        filters={
          <>
            <SearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Search"
              ariaLabel="Search"
            />
            <MultiSelectChipFilter
              label="Component Group"
              options={componentGroups.map((g) => g.id)}
              value={componentGroupSelected}
              onChange={(next) => {
                setComponentGroupSelected(next);
              }}
              optionLabel={(id) => findGroupNameById(id, componentGroups) ?? id}
              width={280}
            />
            <MultiSelectChipFilter
              label="Manufacturer"
              options={[...manufacturerOptions]}
              value={manufacturerSelected}
              onChange={(next) => {
                setManufacturerSelected(next);
              }}
              width={220}
            />
            <ScopeFilterWithInfo
              value={scopeSelected}
              onChange={setScopeSelected}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={includeInactive}
                  onChange={(_e, c) => {
                    toggleIncludeInactive(c);
                  }}
                  inputProps={{
                    "aria-label": "Show inactive component models",
                  }}
                />
              }
              label="Show inactive"
              sx={{ ml: 0, mr: 0 }}
            />
          </>
        }
        settings={
          <>
            <ColumnVisibilityButton
              columns={COMPONENT_MODEL_COLUMNS_META}
              model={columnVisibility}
              onChange={handleColumnVisibilityChange}
            />
            <DensityToggleButton density={density} onChange={setDensity} />
            <Box sx={{ flex: 1 }} />
            {canAdd ? (
              <Button
                aria-label="Add component model"
                onClick={openNew}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                sx={(t) => {
                  const mode = t.palette.mode;
                  const tokens =
                    mode === "dark" ? sidebarTokens.dark : sidebarTokens.light;
                  return {
                    height: 32,
                    minHeight: 32,
                    paddingInline: 10,
                    borderRadius: 1.5,
                    fontSize: typography.size["body-md"],
                    fontWeight: typography.weight.medium,
                    letterSpacing: typography.tracking["body-md"],
                    lineHeight: 1,
                    bgcolor: tokens.selectedBg,
                    color: tokens.selectedFg,
                    textTransform: "none",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: t.palette.primary.light,
                      boxShadow: "none",
                    },
                    "&:active": {
                      bgcolor: tokens.rowPressedBg,
                      color: tokens.selectedFg,
                      boxShadow: "none",
                    },
                    "&:focus-visible": {
                      outline: `2px solid ${t.palette.primary.main}`,
                      outlineOffset: 2,
                    },
                    "& .MuiButton-startIcon": {
                      marginRight: 1,
                      marginLeft: -0.25,
                    },
                  };
                }}
              >
                Add component model
              </Button>
            ) : null}
            {canAddGlobal ? (
              <Button
                aria-label="Add global component model"
                onClick={openNewGlobal}
                startIcon={<PlusIcon fontSize="small" />}
                disableElevation
                variant="outlined"
                sx={{
                  height: 32,
                  minHeight: 32,
                  paddingInline: 10,
                  borderRadius: 1.5,
                  fontSize: typography.size["body-md"],
                  fontWeight: typography.weight.medium,
                  textTransform: "none",
                  boxShadow: "none",
                }}
              >
                Add global component model
              </Button>
            ) : null}
          </>
        }
      />

      {isPending ? (
        <Box
          sx={{
            flex: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "flex-start",
            py: 6,
          }}
        >
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Typography color="error" sx={{ p: 2 }}>
          Couldn&apos;t load component models.
        </Typography>
      ) : data.items.length === 0 ? (
        <ComponentModelsEmptyState onAdd={openNew} canAdd={canAdd} />
      ) : (
        <DataTable<AssetComponentModelExpanded>
          rows={data.items}
          columns={columns}
          ariaLabel="Component Models"
          density={density}
          rowCount={total}
          paginationModel={{ page: page - 1, pageSize }}
          onPaginationModelChange={(m) => {
            const nextPage = m.page + 1;
            if (m.pageSize !== pageSize) {
              setPageSize(m.pageSize);
            } else if (nextPage !== page) {
              setPage(nextPage);
            }
          }}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          columnVisibilityModel={columnVisibility}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          pinnedActionColumn
          getRowClassName={(p) => (!p.row.isActive ? "row--inactive" : "")}
          sx={{
            "& .row--inactive": { opacity: 0.55 },
          }}
        />
      )}

      <DataPageFooter
        total={total}
        entityLabel="component models"
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        totalTestId="component-models-footer-total"
      />

      <AddComponentModelDialog
        open={isNewOpen && canAdd}
        onClose={closeNew}
        categorySuggestions={categoryOptions}
        manufacturerSuggestions={manufacturerOptions}
      />
      <AddComponentModelDialog
        open={isNewGlobalOpen && canAddGlobal}
        onClose={closeNew}
        categorySuggestions={categoryOptions}
        manufacturerSuggestions={manufacturerOptions}
        scope="global"
      />
      <EditComponentModelDialog modelId={modelIdInUrl} onClose={closeModel} />
    </DataPageShell>
  );
}

/**
 * Scope filter with an in-field info icon that opens a popover
 * explaining the `Global` vs `Tenant` distinction. Mirrors the
 * Turbine Models page (TurbineModelsPage `ScopeFilterWithInfo`)
 * verbatim, with copy adjusted from "turbine model" → "component
 * model".
 *
 * Width is bumped from the 130 px default to 170 px so the icon and
 * the chevron both have breathing room without crowding the value.
 */
function ScopeFilterWithInfo({
  value,
  onChange,
}: {
  value: "global" | "tenant" | undefined;
  onChange: (next: "global" | "tenant" | undefined) => void;
}): ReactElement {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const open = Boolean(anchor);
  return (
    <>
      <SingleSelectFilter
        label="Scope"
        width={170}
        value={value ?? "all"}
        options={[
          { value: "all", label: "All", display: "" },
          { value: "global", label: "Global" },
          { value: "tenant", label: "Tenant" },
        ]}
        onChange={(v) => {
          if (v === "global") onChange("global");
          else if (v === "tenant") onChange("tenant");
          else onChange(undefined);
        }}
        endAdornment={
          <Tooltip title="What does Scope mean?">
            <IconButton
              size="small"
              aria-label="Scope explanation"
              onMouseDown={(e) => {
                // Stop the MouseDown so the Select doesn't open its
                // dropdown menu when the user clicks the info icon.
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setAnchor(e.currentTarget);
              }}
              sx={{ mr: 2 }}
            >
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        }
      />
      <Popover
        open={open}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{
          paper: { sx: { p: 2, maxWidth: 420 } },
        }}
      >
        <Typography variant="subtitle2" gutterBottom>
          Scope
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Each component model belongs to either the platform-wide catalog or
          your company&apos;s own tenant.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Global</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          The shared catalog of models maintained by the platform team. You can
          use these models when adding components, but you cannot edit or
          deactivate them from your account.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <strong>Tenant</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Models that live inside your company&apos;s tenant. Anyone in the
          mutate-role set can create a new tenant model; tenant admins can edit
          and deactivate them.
        </Typography>
      </Popover>
    </>
  );
}

function ComponentModelsEmptyState({
  onAdd,
  canAdd,
}: {
  onAdd: () => void;
  canAdd: boolean;
}): ReactElement {
  return (
    <Box
      sx={{
        flex: 1,
        py: 8,
        textAlign: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
      }}
    >
      <BoxIcon sx={{ fontSize: 48, color: "text.secondary" }} aria-hidden />
      <Typography variant="h6" color="text.primary">
        No component models yet.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Add your first tenant-specific component model to get started.
      </Typography>
      {canAdd ? (
        <Button variant="contained" onClick={onAdd}>
          Add component model
        </Button>
      ) : null}
    </Box>
  );
}
